import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import WhatsAppTemplatePreview from "../../AutoReplyBuilder/components/WhatsAppTemplatePreview";

const countBodyPlaceholders = body => {
  if (!body) return 0;
  const s = String(body);
  const positional = s.match(/\{\{\s*\d+\s*\}\}/g) || [];
  const named = s.match(/\{\{\s*\}\}/g) || [];
  return positional.length + named.length;
};

const normalizeTemplateButton = (button, index) => {
  const text = String(
    button?.text ?? button?.Text ?? button?.label ?? button?.Label ?? ""
  ).trim();
  const type = String(button?.type ?? button?.Type ?? "QUICK_REPLY")
    .trim()
    .toUpperCase();
  const subType = String(button?.subType ?? button?.SubType ?? "")
    .trim()
    .toLowerCase();
  const value = String(
    button?.value ??
      button?.Value ??
      button?.parameterValue ??
      button?.ParameterValue ??
      button?.url ??
      button?.Url ??
      ""
  ).trim();

  return {
    text,
    type: type || "QUICK_REPLY",
    subType,
    value,
    parameterValue: value,
    index:
      typeof button?.index === "number"
        ? button.index
        : typeof button?.Index === "number"
          ? button.Index
          : index,
  };
};

export default function CTAFlowNodeEditor({ node, onClose, onSave }) {
  const [form, setForm] = useState({
    templateName: "",
    templateType: "",
    messageBody: "",
    triggerButtonText: "",
    triggerButtonType: "cta",
    requiredTag: "",
    requiredSource: "",
    useProfileName: false,
    profileNameSlot: 1,
    buttons: [],
    headerMediaUrl: "",
    bodyParams: [],
    urlButtonParams: ["", "", ""],
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const hydratedRef = useRef(false);
  const hydratedNodeIdRef = useRef(null);

  const headerKind = useMemo(() => {
    const hk = selectedTemplate?.headerKind;
    if (hk) return String(hk).trim().toLowerCase();

    const templateType = String(form.templateType || "").trim().toLowerCase();
    if (templateType === "image_template") return "image";
    if (templateType === "video_template") return "video";
    if (templateType === "document_template") return "document";
    return "none";
  }, [selectedTemplate, form.templateType]);

  const needsHeaderUrl =
    headerKind === "image" || headerKind === "video" || headerKind === "document";
  const topOffset = "var(--app-shell-topbar-size, 4rem)";

  const bodyPlaceholderCount = useMemo(
    () => countBodyPlaceholders(form.messageBody),
    [form.messageBody]
  );

  const dynamicUrlButtons = useMemo(() => {
    const btns = Array.isArray(form.buttons) ? form.buttons : [];
    return btns
      .map((button, index) => normalizeTemplateButton(button, index))
      .filter(button => {
        const isUrl = button.type === "URL" || button.subType === "url";
        const isDynamic = String(button.parameterValue || button.value || "").includes("{{");
        return isUrl && isDynamic;
      })
      .filter(button => button.index >= 0 && button.index <= 2)
      .sort((a, b) => a.index - b.index);
  }, [form.buttons]);

  useEffect(() => {
    if (!node) {
      hydratedRef.current = false;
      hydratedNodeIdRef.current = null;
      return;
    }

    if (hydratedNodeIdRef.current === node.id && hydratedRef.current) {
      return;
    }

    const { data } = node;
    const next = {
      templateName: data.templateName || "",
      templateType: data.templateType || "",
      messageBody: data.messageBody || "",
      triggerButtonText: data.triggerButtonText || "",
      triggerButtonType: data.triggerButtonType || "cta",
      requiredTag: data.requiredTag || "",
      requiredSource: data.requiredSource || "",
      useProfileName: !!data.useProfileName,
      profileNameSlot: data.profileNameSlot || 1,
      buttons: Array.isArray(data.buttons)
        ? data.buttons.map((button, index) => normalizeTemplateButton(button, index))
        : [],
      headerMediaUrl: data.headerMediaUrl || "",
      bodyParams: Array.isArray(data.bodyParams)
        ? data.bodyParams.map(value => String(value || ""))
        : [],
      urlButtonParams: Array.isArray(data.urlButtonParams)
        ? Array.from(
            { length: 3 },
            (_, index) => String(data.urlButtonParams[index] || "")
          )
        : ["", "", ""],
    };

    setForm(next);
    setSelectedTemplate(
      next.templateName
        ? {
            name: next.templateName,
            languageCode: "",
            body: next.messageBody,
            buttonParams: next.buttons,
            templateType: next.templateType,
            headerKind:
              next.templateType === "image_template"
                ? "image"
                : next.templateType === "video_template"
                  ? "video"
                  : next.templateType === "document_template"
                    ? "document"
                    : "none",
          }
        : null
    );
    hydratedNodeIdRef.current = node.id;
    hydratedRef.current = true;
  }, [node]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = () => {
    onSave?.(form);
    onClose?.();
  };

  if (!node) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60]" style={{ top: topOffset }}>
      <div
        className="absolute inset-0 bg-slate-950/35 backdrop-blur-[1px]"
        onClick={onClose}
      />

      <div className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-slate-200 bg-white shadow-[-8px_0_32px_rgba(0,0,0,0.08)] sm:w-[480px]">
        {/* Pinned header */}
        <div className="flex items-start justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4">
          <div>
            <h2 className="text-[15px] font-bold tracking-tight text-slate-800 italic">
              {form.templateName || "CTA Step"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {selectedTemplate && (
            <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3">
              <WhatsAppTemplatePreview
                template={selectedTemplate}
                headerMediaUrl={form.headerMediaUrl}
                bodyParams={form.bodyParams}
                useProfileName={form.useProfileName}
                profileNameSlot={form.profileNameSlot}
              />
            </div>
          )}

          <div className="space-y-3 rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                Required fields
              </div>
            </div>

            {bodyPlaceholderCount > 0 ? (
              <div className="space-y-3 rounded-lg border border-slate-100 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Body placeholders
                    </div>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {bodyPlaceholderCount} required
                  </span>
                </div>

                <div className="space-y-4">
                  {Array.from({ length: bodyPlaceholderCount }).map((_, idx) => (
                    <div key={idx} className="space-y-1.5">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          Placeholder {"{{"}{idx + 1}{"}}"}
                        </span>
                      </div>
                      <input
                        type="text"
                        value={form.bodyParams[idx] || ""}
                        onChange={e => {
                          const next = [...form.bodyParams];
                          next[idx] = e.target.value;
                          setForm(prev => ({ ...prev, bodyParams: next }));
                        }}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                        placeholder={`Enter the value for {{${idx + 1}}}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {dynamicUrlButtons.length > 0 ? (
              <div className="space-y-3 rounded-lg border border-slate-100 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Dynamic URL buttons
                    </div>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {dynamicUrlButtons.length} required
                  </span>
                </div>

                <div className="space-y-4">
                  {dynamicUrlButtons.map(btn => {
                    const idx = btn.index;
                    const value = Array.isArray(form.urlButtonParams)
                      ? form.urlButtonParams[idx] || ""
                      : "";

                    return (
                      <div key={`${idx}-${btn.text || "button"}`} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            {btn.text || `Button ${idx + 1}`}
                          </span>
                        </div>
                        <input
                          type="text"
                          value={value}
                          onChange={e => {
                            const next = Array.isArray(form.urlButtonParams)
                              ? [...form.urlButtonParams]
                              : ["", "", ""];
                            next[idx] = e.target.value;
                            setForm(prev => ({ ...prev, urlButtonParams: next }));
                          }}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                          placeholder={`Value for ${btn.text || `Button ${idx + 1}`}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {needsHeaderUrl && (
              <div className="space-y-2 rounded-lg border border-slate-100 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Header {headerKind.charAt(0).toUpperCase() + headerKind.slice(1)} URL
                    </div>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    required
                  </span>
                </div>
                <input
                  type="text"
                  name="headerMediaUrl"
                  value={form.headerMediaUrl}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                  placeholder="https://..."
                />
              </div>
            )}
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-5 py-4">
          <Button
            variant="ghost"
            className="flex-1 text-slate-500 hover:bg-slate-100"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="shrink-0 bg-emerald-600 px-5 text-white shadow-md hover:bg-emerald-700"
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

